import React, { useEffect, useState } from "react";
import { Phone, Mail, MapPin, Clock, ExternalLink, Car, Train, Bus } from "lucide-react";
import { dict } from "../i18n";
import { fetchContent } from "../lib/api";
import SEO from "../components/SEO";
import InquiryForm from "../components/InquiryForm";
import FAQSection from "../components/FAQSection";

const TRANSIT_ICONS = { parking: Car, tram: Train, bus_ravala: Bus, bus_kunstiakadeemia: Bus };

export default function Contact({ lang }) {
  const t = dict[lang];
  const [clinic, setClinic] = useState(null);

  useEffect(() => {
    fetchContent().then(setClinic).catch(() => {});
  }, []);

  const tallinn = (clinic?.locations || []).find((l) => l.id === "tallinn");
  const transit = tallinn?.transit;

  return (
    <>
      <SEO lang={lang} page="contact" />
      <section className="container-page pt-12 pb-8" data-testid="contact-page">
        <div className="chip mb-3">{t.sections.contact.tag}</div>
        <h1 className="section-title">{t.sections.contact.title}</h1>
      </section>

      <section className="container-page pb-16 grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3">
          <InquiryForm lang={lang} />
        </div>

        <div className="lg:col-span-2 space-y-4">
          <div className="card-clinic">
            <div className="chip mb-3">{t.nav.contact}</div>
            <div className="space-y-3 text-[14px]">
              <a href="tel:+37255665451" className="flex items-center gap-3 text-surface-ink hover:text-sapphire-700">
                <Phone className="h-4 w-4 text-sapphire-700" /> +372 5566 5451
              </a>
              <a href="tel:+37255665135" className="flex items-center gap-3 text-surface-ink hover:text-sapphire-700">
                <Phone className="h-4 w-4 text-sapphire-700" /> +372 5566 5135
              </a>
              <a href="mailto:info@endokrinoloog.ee" className="flex items-center gap-3 text-surface-ink hover:text-sapphire-700">
                <Mail className="h-4 w-4 text-sapphire-700" /> info@endokrinoloog.ee
              </a>
              <a
                href="https://veebiregistratuur.ee/#/tallinna-endokrinoloogia-kliinik/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 text-sapphire-700 font-medium"
                data-testid="contact-digireg-link"
              >
                <ExternalLink className="h-4 w-4" /> {t.footer.digital}
              </a>
            </div>
          </div>

          {(clinic?.locations || []).map((loc) => (
            <div key={loc.id} className="card-clinic" data-testid={`contact-location-${loc.id}`}>
              <div className="text-[12px] uppercase tracking-wider font-semibold text-teal-500">{loc.city[lang]}</div>
              <div className="mt-2 space-y-2 text-[14px]">
                <div className="flex items-start gap-2"><MapPin className="h-4 w-4 mt-0.5 text-surface-inkSoft" />{loc.address}</div>
                {loc.schedule && loc.schedule.length > 0 ? (
                  <div className="space-y-1.5">
                    {loc.schedule.map((s, si) => (
                      <div key={si} className="flex items-start gap-2 text-[13px]">
                        <Clock className="h-3.5 w-3.5 mt-1 text-surface-inkSoft shrink-0" />
                        <div>
                          <div className="font-medium text-surface-ink">{s.role[lang]}</div>
                          <div className="text-surface-inkSoft">{s.days[lang]}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex items-center gap-2"><Clock className="h-4 w-4 text-surface-inkSoft" />{loc.hours[lang]}</div>
                )}
                {loc.note && <div className="text-[12px] text-surface-inkSoft italic">{loc.note[lang]}</div>}
              </div>
            </div>
          ))}
        </div>
      </section>

      {transit && (
        <section className="container-page pb-20" data-testid="transit-section">
          <div className="chip mb-3">{t.sections.transit.tag}</div>
          <h2 className="section-title mb-3">{t.sections.transit.title}</h2>
          <p className="text-surface-inkSoft text-[15px] mb-8 max-w-2xl">
            Tartu mnt 16, 3. korrus, Tallinn — sissepääs Rävala puiesteelt
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5">
            {["parking", "tram", "bus_ravala", "bus_kunstiakadeemia"].map((key) => {
              const blk = transit[key];
              if (!blk) return null;
              const Icon = TRANSIT_ICONS[key] || Car;
              return (
                <div key={key} className="card-clinic" data-testid={`transit-${key}`}>
                  <div className="h-11 w-11 rounded-xl bg-sapphire-50 flex items-center justify-center text-sapphire-700 mb-3">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="text-[15px] font-semibold text-surface-ink mb-3">{blk.title[lang]}</h3>
                  <ul className="space-y-1.5 text-[13px] text-surface-inkSoft leading-relaxed">
                    {blk.items.map((item, ii) => (
                      <li key={ii} className="flex gap-2">
                        <span className="text-teal-500 mt-0.5">•</span>
                        <span>{typeof item === "string" ? item : item[lang] || item.et}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </section>
      )}

      <FAQSection lang={lang} />
    </>
  );
}
